import React from 'react';
import { motion } from 'framer-motion';
import { assets } from '../lib/assets';

// CSS-rendered Indian nameplate — rendered over images so we can show
// authentic Devanagari dedication without relying on stock-photo text.
function HindiNameplate({ top = 'अम्मा के नाम', bottom = 'For Amma · 2024' }) {
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-[42%] -translate-y-1/2 rotate-[-3deg]">
      <div
        className="relative px-6 py-3 min-w-[180px] text-center shadow-xl"
        style={{
          background: 'linear-gradient(180deg, #6b4a2e 0%, #4e3420 100%)',
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
        }}
      >
        <div className="absolute top-1 left-2 w-1.5 h-1.5 rounded-full bg-[#d7b483]/80" />
        <div className="absolute top-1 right-2 w-1.5 h-1.5 rounded-full bg-[#d7b483]/80" />
        <div className="absolute bottom-1 left-2 w-1.5 h-1.5 rounded-full bg-[#d7b483]/80" />
        <div className="absolute bottom-1 right-2 w-1.5 h-1.5 rounded-full bg-[#d7b483]/80" />
        <div
          className="text-[#f2d9a8] text-lg md:text-xl leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.02em' }}
        >
          {top}
        </div>
        <div className="text-[#c69c6d] italic text-[10px] md:text-xs mt-0.5 tracking-wider">
          {bottom}
        </div>
      </div>
    </div>
  );
}

const moments = [
  { key: 'plaque', img: assets.plaque, title: 'The plaque', copy: 'Hand-carved wood. Their name, under sun and season.' },
  { key: 'farmer', img: assets.farmerCare, title: 'The farmer', copy: "Meet Ganesh-kaka. He'll watch over your tree." },
  { key: 'planting', img: assets.planting, title: 'The first planting', copy: 'Photographed the day it touches earth.' },
  { key: 'harvest', img: assets.fruitBasket, title: 'The harvest', copy: 'Crate after crate, delivered with their name on it.' },
];

export default function Storytelling() {
  return (
    <section id="story" className="bg-brand-bg2 py-28 md:py-36" data-testid="story-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-14">
          <p className="text-brand-gold tracking-[0.25em] uppercase text-xs mb-5 font-medium">A living journey</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight font-light">
            Not a product. <em className="text-brand-forest">A relationship.</em>
          </h2>
          <p className="mt-5 text-brand-ink2 text-lg leading-relaxed">
            From the moment your tree is named, it enters a quiet correspondence with you.
            Here's what arrives, softly, across the years.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {moments.map((m, i) => (
            <motion.div
              key={m.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.9, delay: i * 0.1 }}
              className="group relative rounded-3xl overflow-hidden aspect-[4/3]"
              data-testid={`story-moment-${i}`}
            >
              <img src={m.img} alt={m.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/10 to-transparent" />
              <div className="absolute left-7 right-7 bottom-7">
                <div className="font-serif italic text-brand-gold text-sm mb-1 tracking-wide">Chapter 0{i + 1}</div>
                <div className="font-serif text-3xl md:text-4xl text-brand-bg leading-tight mb-2">{m.title}</div>
                <div className="text-brand-bg/85 max-w-md leading-relaxed">{m.copy}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
